import React from 'react';
import {
  Box, Button, Card, CardContent, Chip, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import type { Cadence, DCAStream, PaceCheck, StreamStatus } from '../../services/investment-plan-service';
import { baseMoney } from '../../stores/currency-store';

const CADENCE: Record<Cadence, string> = {
  trading_daily: 'Every trading day',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
};

const STATUS: Record<StreamStatus['status'], { label: string; color: string }> = {
  on_track: { label: 'On track', color: 'var(--inflow-text)' },
  missed: { label: 'Missed', color: 'var(--warn-text)' },
  paused: { label: 'Paused', color: 'var(--text-secondary)' },
  amount_unknown: { label: 'Amount unknown', color: 'var(--info-text)' },
  not_started: { label: 'Not started', color: 'var(--text-secondary)' },
  no_recent_data: { label: 'No recent trade data', color: 'var(--text-secondary)' },
  reminder_due: { label: 'Reminder due', color: 'var(--warn-text)' },
};

/** "XUU.TO 53% ($320)": the share, with the dollars when the source gave them. */
export function mixLabel(m: DCAStream['mix'][number]): string {
  const pct = Number(m.pct);
  const amt = m.amount === undefined || m.amount === null || m.amount === '' ? null : Number(m.amount);
  return `${m.symbol || m.asset_class} ${Number.isFinite(pct) ? Math.round(pct) : m.pct}%` +
    (amt !== null && Number.isFinite(amt) ? ` ($${amt.toLocaleString()})` : '');
}

/**
 * The glide path's recurring buys (issue #31 follow-up): each stream with its
 * own status (on track / missed with dates / paused / amount unknown), its
 * amount and cadence editable in the plan form, and the combined pace against
 * what the pool needs by the target end date.
 */
export const StreamsCard: React.FC<{
  streams: DCAStream[];
  statuses?: StreamStatus[];
  pace?: PaceCheck | null;
  onChange: (streams: DCAStream[]) => void;
  onPause?: (id: string, paused: boolean) => void;
  errors?: Record<string, string>;
}> = ({ streams, statuses = [], pace, onChange, onPause, errors = {} }) => {
  const byId = new Map(statuses.map((s) => [s.id, s]));
  const update = (i: number, patch: Partial<DCAStream>) =>
    onChange(streams.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <Card sx={{ mb: 3 }} component="section" aria-label="DCA streams">
      <CardContent>
        <Typography variant="h6" component="h2">DCA streams</Typography>
        {pace && (
          <Typography variant="body2" sx={{ color: pace.on_pace ? 'var(--inflow-text)' : 'var(--warn-text)', mb: 1 }}
            data-testid="pace-line">
            Combined {baseMoney(pace.combined_daily, 2)}/trading day vs {baseMoney(pace.required_daily, 2)} needed to deploy{' '}
            {baseMoney(pace.pool, 0)} by {pace.target_end}
            {!pace.on_pace && pace.raise_stream_name ? ` — raise “${pace.raise_stream_name}” to ${baseMoney(pace.raise_to, 0)}/day` : ''}
          </Typography>
        )}
        {streams.length === 0 && (
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>No streams: the glide path uses the plan's single rate.</Typography>
        )}
        {streams.map((s, i) => {
          const st = byId.get(s.id);
          const tone = st ? STATUS[st.status] : null;
          return (
            <Box key={s.id || i} sx={{ py: 1.25, borderTop: i ? '1px solid var(--border-soft)' : 'none' }} aria-label={`Stream ${s.name}`}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: 650 }}>{s.name}</Typography>
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{s.broker}</Typography>
                {tone && (
                  <Chip size="small" variant="outlined" label={tone.label}
                    sx={{ color: tone.color, borderColor: tone.color, fontWeight: 600 }} />
                )}
                {st && st.daily_pace !== '0' && (
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>≈ {baseMoney(st.daily_pace, 2)}/trading day</Typography>
                )}
                {onPause && s.id && (
                  <Button size="small" sx={{ ml: 'auto' }} onClick={() => onPause(s.id, !s.paused)}>
                    {s.paused ? 'Resume' : 'Pause'}
                  </Button>
                )}
              </Stack>
              <Stack direction="row" gap={1.5} sx={{ mt: 1 }} flexWrap="wrap">
                <TextField size="small" label="Amount per buy" sx={{ width: 160 }}
                  value={s.amount ?? ''} placeholder="unknown"
                  error={!!errors[`streams.${i}`]} helperText={errors[`streams.${i}`] || (st?.amount_inferred ? `inferred ${st.amount}` : '')}
                  inputProps={{ 'aria-label': `${s.name} amount` }}
                  onChange={(e) => update(i, { amount: e.target.value === '' ? null : e.target.value })} />
                <TextField size="small" select label="Cadence" sx={{ width: 180 }} value={s.cadence}
                  inputProps={{ 'aria-label': `${s.name} cadence` }}
                  onChange={(e) => update(i, { cadence: e.target.value as Cadence })}>
                  {(Object.keys(CADENCE) as Cadence[]).map((c) => <MenuItem key={c} value={c}>{CADENCE[c]}</MenuItem>)}
                </TextField>
                <Typography variant="caption" sx={{ alignSelf: 'center', color: 'var(--text-secondary)' }}>
                  {s.mix.filter((m) => Number(m.pct) > 0).map(mixLabel).join(' · ')}
                </Typography>
              </Stack>
              {st && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--text-secondary)' }}>
                  {st.hit}/{st.occurrences} buys since {st.window_start} · {baseMoney(st.actual, 0)} of {baseMoney(st.expected, 0)}
                  {st.last_buy ? ` · last ${st.last_buy}` : ''}
                  {st.missed_dates.length > 0 ? ` · missed ${st.missed_dates.slice(-5).join(', ')}` : ''}
                  {(st.unverified_dates ?? []).length > 0
                    ? ` · not yet verifiable (trades imported through ${st.verified_through || 'never'}): ${(st.unverified_dates ?? []).length} date(s)`
                    : ''}
                  {st.days_since_last_buy !== undefined ? ` · ${st.days_since_last_buy} days since last buy` : ''}
                  {st.note ? ` · ${st.note}` : ''}
                </Typography>
              )}
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};
